/* ═══════════════════════════════════════════════════════════════
   Token Components — Drag-and-drop token UI elements
   Depends on: constants.ts, utils.ts (loaded first)
   ═══════════════════════════════════════════════════════════════ */

import React, { useState as tUseState, useRef as tUseRef, useEffect as tUseEffect } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, CornerDownLeft, Pencil, X } from 'lucide-react';
import { TOKENS, ALL_TOKENS } from '../constants';
import {
  tokBase,
  tokLabel,
  tokPreview,
  tokModStyle,
  parseLineParams,
  parseImageId,
  isValidMod,
  normalizeForPreview,
  tokSpacing,
  ptPx,
  toggleTokenMod,
  setCaseMod,
  applyCaseModText,
  setTokenColor,
  setTokenSpace,
  setTokenSize,
  getTokenSize,
  placeholderMemory,
  placeholderValue,
  isProjectForma,
} from '../utils';
import { isNumberedPlaceholder, placeholderId } from '../../shared/forma-placeholders';
import { Hint } from './ui/tooltip';
import { FORMA_TIPS } from '../formaTooltips';
import type { EmbeddedImage } from '../../shared/types';

/* ─── Canvas selection model ───────────────────────────────────────
   ONE object selected at a time across the whole canvas (token / level /
   zone), and at most ONE context menu open. Left-click = select; right-click
   = select + open context menu. Keys: `token:<zone>:<idx>`, `level:<i>`,
   `zone:<region>`. Consumed by ZoneChip / LevelRow / the zone bands so the
   "single selection, single menu" rule is enforced by construction. */
export interface CanvasSelectValue {
  selKey: string | null;
  menuKey: string | null;
  /** All currently multi-selected token keys (for copy / paste / delete). */
  selectedTokens: string[];
  select: (
    key: string | null,
    opts?: { menu?: boolean; additive?: boolean; keepSelection?: boolean; x?: number; y?: number }
  ) => void;
  /** Apply a value-transform to EVERY selected token, across any sections, at once. */
  applyToSelection: (fn: (value: string) => string) => void;
  /** Move (or copy) tokens (by key) into a target zone — the drag-drop path for
   *  moving a whole selection across sections/pages. */
  moveTokens: (targetZoneKey: string, keys: string[], copy: boolean) => void;
  /** Select a whole SET of token keys at once — what a marquee drag on the
   *  canvas produces. `additive` unions with the current selection (Ctrl /
   *  Shift). Repeated `select(k, {additive})` calls can't express this: that
   *  path TOGGLES, so a marquee over an already-selected token would
   *  deselect it. Defaults to a no-op for callers that never marquee. */
  selectMany: (keys: string[], additive?: boolean) => void;
}
export const CanvasSelectionContext = React.createContext<CanvasSelectValue>({
  selKey: null,
  menuKey: null,
  selectedTokens: [],
  select: () => {},
  applyToSelection: () => {},
  moveTokens: () => {},
  selectMany: () => {},
});

/* ─── Token clipboard (copy / cut / paste triad — DESIGN.md §4) ───
   Provided by PageCanvas; consumed by the chip format menu and the slot
   context menu, so every clipboard action is reachable BOTH by right-click
   and by the keyboard shortcuts. One clipboard, every zone. */
export interface TokenClipboardOps {
  /** Copy the given token keys' values to the clipboard. */
  copyKeys: (keys: string[]) => void;
  /** Copy + delete the given token keys. */
  cutKeys: (keys: string[]) => void;
  /** Copy a whole slot's tokens. */
  copySlot: (zoneKey: string) => void;
  /** Copy + clear a whole slot. */
  cutSlot: (zoneKey: string) => void;
  /** Paste relative to a target: after `token:<zone>:<idx>`, or appended
   *  into `slot:<zoneKey>` (works on empty slots). */
  pasteAt: (targetKey: string) => void;
  canPaste: () => boolean;
}
export const TokenClipboardContext = React.createContext<TokenClipboardOps | null>(null);

/* ─── One context menu for every token SLOT / marker on the canvas ───
   The slot menu used to live inline inside DropZone. The Studio's sheet now
   needs the identical menu on its own L/C/R column regions and on its line-
   break markers, so it is ONE component (DESIGN.md §5 "one of everything")
   instead of three copies. Portalled to <body>, `position: fixed`,
   viewport-clamped, dismissed on mousedown-outside or Escape — never on
   hover-leave (DESIGN.md §4 ABSOLUTE). */
export interface TokenMenuItem {
  label: string;
  enabled: boolean;
  run: () => void;
  danger?: boolean;
}

export function TokenSlotMenu({
  at,
  items,
  label,
  onClose,
}: {
  at: { x: number; y: number };
  items: TokenMenuItem[];
  /** Accessible name for the menu itself. */
  label: string;
  onClose: () => void;
}) {
  const ref = tUseRef<HTMLDivElement>(null);
  tUseEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', close, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // `.dsg-menu` / `.dsg-menu-item` — the SAME context menu the Designer and
  // the Studio already use (BlockMenu, the Workspace menu). One menu in the
  // app, not a second hand-rolled one (DESIGN.md §5 "one of everything").
  return createPortal(
    <div
      ref={(el) => {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
        if (!el) return;
        // Clamp/flip inside the viewport — never clipped by overflow or zoom.
        const b = el.getBoundingClientRect();
        if (b.right > window.innerWidth - 8) el.style.left = `${window.innerWidth - b.width - 8}px`;
        if (b.bottom > window.innerHeight - 8)
          el.style.top = `${window.innerHeight - b.height - 8}px`;
      }}
      onMouseDown={(e) => e.stopPropagation()}
      role="menu"
      aria-label={label}
      className="dsg-menu dsg-tokenmenu"
      style={{ top: at.y, left: at.x }}
    >
      {items.map((a) => (
        <button
          key={a.label}
          role="menuitem"
          className={`dsg-menu-item${a.danger ? ' is-danger' : ''}`}
          disabled={!a.enabled}
          onClick={(e) => {
            e.stopPropagation();
            a.run();
            onClose();
          }}
        >
          {a.label}
        </button>
      ))}
    </div>,
    document.body
  );
}

/** The three clipboard actions every token column offers, in one place so the
 *  panel's DropZone and the Studio's on-sheet column regions can never drift
 *  apart (copy/cut/paste triad — DESIGN.md §4 ABSOLUTE). */
export function slotClipboardItems(
  clip: TokenClipboardOps,
  zoneKey: string,
  hasTokens: boolean
): TokenMenuItem[] {
  return [
    { label: 'Paste', enabled: clip.canPaste(), run: () => clip.pasteAt(`slot:${zoneKey}`) },
    { label: 'Copy all', enabled: hasTokens, run: () => clip.copySlot(zoneKey) },
    { label: 'Cut all', enabled: hasTokens, run: () => clip.cutSlot(zoneKey) },
  ];
}

/* ─── Drag ghost — replaces the browser's washed-out default snapshot
       with a solid, lifted chip so dragging feels like holding an object.
       Neutral dark slate in both themes; mono only for literal text. ─── */
function setTokenDragGhost(e: React.DragEvent, label: string, mono?: boolean) {
  try {
    const el = document.createElement('div');
    el.textContent = label;
    el.style.cssText = [
      'position:fixed',
      'top:-200px',
      'left:-200px',
      'padding:6px 12px',
      'border-radius:6px',
      mono
        ? "font:500 11px 'JetBrains Mono',monospace"
        : "font:500 12px 'DM Sans',system-ui,sans-serif",
      'background:#23272e',
      'color:#fff',
      'letter-spacing:.01em',
      'box-shadow:0 8px 22px rgba(0,0,0,.28)',
      'pointer-events:none',
      'z-index:99999',
    ].join(';');
    document.body.appendChild(el);
    e.dataTransfer.setDragImage(el, Math.floor(el.offsetWidth / 2), el.offsetHeight + 6);
    requestAnimationFrame(() => {
      try {
        document.body.removeChild(el);
      } catch {}
    });
  } catch {}
}

/* ─── Token Chip (in the dock) — one quiet treatment for every token.
       Neutral at rest, neutral lift on hover; the accent appears only
       while dragging, so color always means "this is in motion". ─── */
function PaletteChip({
  value,
  label,
  dashed,
  linked,
  title,
  onDragStart,
  onDragEnd,
  onInsert,
}: {
  value: string;
  label?: string;
  dashed?: boolean;
  /** Project-linked token — shows a small link glyph (fills from project data) */
  linked?: boolean;
  title?: string;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  /** Optional click-to-insert (Designer's Design mode). Additive: absent →
      the chip behaves exactly as before (drag only). */
  onInsert?: () => void;
}) {
  const text = label || tokLabel(value);
  const [hover, setHover] = tUseState(false);
  const [grabbing, setGrabbing] = tUseState(false);
  return (
    <Hint
      text={title || (onInsert ? 'Drag onto the page, or click to add it.' : 'Drag onto the page.')}
    >
      <span
        tabIndex={0}
        role={onInsert ? 'button' : undefined}
        onClick={onInsert}
        onKeyDown={
          onInsert
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onInsert();
                }
              }
            : undefined
        }
        draggable
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onDragStart={(e) => {
          e.dataTransfer.setData('application/json', JSON.stringify({ type: 'token', value }));
          e.dataTransfer.effectAllowed = 'copy';
          setTokenDragGhost(e, text);
          setGrabbing(true);
          if (onDragStart) onDragStart();
        }}
        onDragEnd={() => {
          setGrabbing(false);
          if (onDragEnd) onDragEnd();
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          height: 26,
          padding: linked ? '0 10px 0 8px' : '0 10px',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: '.01em',
          background: grabbing
            ? 'var(--ach)'
            : hover
              ? 'var(--color-hover, var(--sa))'
              : 'var(--sf)',
          color: 'var(--tx)',
          border: `1px ${dashed ? 'dashed' : 'solid'} ${
            grabbing ? 'var(--ac)' : hover ? 'var(--color-border-strong, var(--bd))' : 'var(--bd)'
          }`,
          cursor: grabbing ? 'grabbing' : 'grab',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          transform: hover && !grabbing ? 'translateY(-1px)' : 'none',
          boxShadow: hover && !grabbing ? 'var(--shadow-sm)' : 'none',
          transition:
            'transform .12s ease, box-shadow .12s ease, background .12s ease, border-color .12s ease',
        }}
      >
        {linked && (
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            style={{ flexShrink: 0, opacity: 0.55 }}
          >
            <path
              d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
        {/* A glyph-led label (↵ Return) renders its symbol at a legible size —
            at the chip's text size the arrow read as a speck (Josh, 2026-08-13). */}
        {text.startsWith('↵') ? (
          <>
            <span className="sf-chip-glyph" aria-hidden="true">
              {'↵'}
            </span>
            {text.slice(1).trimStart()}
          </>
        ) : (
          text
        )}
      </span>
    </Hint>
  );
}

/* ─── Token format menu — the SAME popover for every token, whichever
       surface it's opened from: the panel's ZoneChip (below) and the
       Studio's on-sheet context menu (paper.tsx, DESIGN.md §4 "Order 1",
       2026-08-13) both render this ONE component, so "right-click a token"
       means the same controls everywhere. Reads CanvasSelectionContext /
       TokenClipboardContext itself — callers only supply what's specific
       to THIS token (its value, its key, whether it's editable text, and
       an anchor to position against). Dismisses on mousedown-outside AND
       Escape (DESIGN.md §4 ABSOLUTE). ─── */
export function TokenFormatMenu({
  value,
  myKey,
  isCustom,
  sizeAware,
  onEdit,
  onRemove,
  onInsertLineBreak,
  onSetFixedDate,
  anchorRef,
}: {
  value: string;
  myKey: string;
  isCustom: boolean;
  sizeAware?: boolean;
  onEdit?: () => void;
  onRemove?: () => void;
  /** Start a new line after this token. Present only where a zone can hold
   *  line breaks (the Studio's sheet) — omitted, the action isn't rendered
   *  and the menu is exactly what it has always been. */
  onInsertLineBreak?: () => void;
  /** Swap a live `{Date}` for a FIXED, frozen date — the classic canvas's
   *  "Use Date" picker, which was the only way it ever stamped a date.
   *  Present only on a `{Date}` token, on a surface that can edit it. */
  onSetFixedDate?: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}) {
  const canvasSel = React.useContext(CanvasSelectionContext);
  const clip = React.useContext(TokenClipboardContext);
  const menuRef = tUseRef<HTMLDivElement>(null);

  function parseMods(v: string) {
    const mods = new Set<string>();
    const pipeIdx = v.lastIndexOf('|');
    if (pipeIdx <= 0) return mods;
    let modStr = v.slice(pipeIdx + 1);
    if (modStr.endsWith('}')) modStr = modStr.slice(0, -1);
    const cands = modStr
      .toLowerCase()
      .split(',')
      .map((s) => s.trim());
    if (cands.every((m) => isValidMod(m))) cands.forEach((m) => mods.add(m));
    return mods;
  }
  function getModColor(mods: Set<string>): string | null {
    for (const m of mods) if (m.startsWith('color:')) return m.slice(6);
    return null;
  }
  function getSpace(mods: Set<string>, which: 'sb' | 'sa'): number {
    for (const m of mods) if (m.startsWith(which + ':')) return parseFloat(m.slice(3)) || 0;
    return 0;
  }
  const currentMods = parseMods(value);

  function toggleMod(mod: string) {
    const willEnable = !currentMods.has(mod);
    canvasSel.applyToSelection((v) => {
      const has = parseMods(v).has(mod);
      return has === willEnable ? v : toggleTokenMod(v, mod);
    });
  }
  // Case mods (ALL CAPS / Title Case / Sentence case) are mutually exclusive
  // — a token prints in exactly one, so setting one clears the other two.
  function toggleCase(mode: 'caps' | 'titlecase' | 'sentencecase') {
    const willEnable = !currentMods.has(mode);
    canvasSel.applyToSelection((v) => setCaseMod(v, willEnable ? mode : null));
  }
  function setColor(hex: string | null) {
    canvasSel.applyToSelection((v) => setTokenColor(v, hex));
  }

  const clipTargetKeys = () =>
    canvasSel.selectedTokens.includes(myKey) && canvasSel.selectedTokens.length > 1
      ? canvasSel.selectedTokens
      : [myKey];

  // Dismiss on mousedown-outside (never hover-leave, never mouseup-outside —
  // DESIGN.md §4 ABSOLUTE) and Escape.
  tUseEffect(() => {
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      canvasSel.select(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') canvasSel.select(null);
    };
    document.addEventListener('mousedown', close, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close, true);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return createPortal(
    (() => {
      const r = anchorRef.current?.getBoundingClientRect();
      const est = { w: 480, h: 44 };
      const left = r ? Math.max(8, Math.min(r.left, window.innerWidth - est.w - 8)) : 8;
      const below = r ? r.bottom + 4 : 8;
      const flipAbove = r ? r.bottom + est.h + 8 > window.innerHeight : false;
      const top = r ? (flipAbove ? r.top - est.h - 4 : below) : 8;
      const color = getModColor(currentMods);
      const sep = <span className="dsg-tokenfmt-sep" aria-hidden="true" />;
      return (
        <div
          ref={(el) => {
            (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
            if (!el) return;
            const b = el.getBoundingClientRect();
            let nl = b.left;
            let nt = b.top;
            if (b.right > window.innerWidth - 8) nl = window.innerWidth - b.width - 8;
            if (nl < 8) nl = 8;
            if (b.bottom > window.innerHeight - 8) nt = window.innerHeight - b.height - 8;
            if (nt < 8) nt = 8;
            if (nl !== b.left) el.style.left = nl + 'px';
            if (nt !== b.top) el.style.top = nt + 'px';
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          role="menu"
          aria-label="Token formatting"
          className="dsg-menu dsg-tokenmenu dsg-tokenfmt"
          style={{ top, left }}
        >
          {[
            { k: 'bold', l: 'B', title: 'Bold' },
            { k: 'italic', l: 'I', title: 'Italic' },
            { k: 'underline', l: 'U', title: 'Underline' },
          ].map((m) => (
            <button
              key={m.k}
              type="button"
              title={m.title}
              aria-label={m.title}
              aria-pressed={currentMods.has(m.k)}
              className={`dsg-tokenfmt-btn is-${m.k}${currentMods.has(m.k) ? ' is-on' : ''}`}
              onClick={() => toggleMod(m.k)}
            >
              {m.l}
            </button>
          ))}
          {sep}
          <span className="dsg-tokenfmt-case-group" role="group" aria-label="Text case">
            {(
              [
                { k: 'caps', l: 'AA', title: 'ALL CAPS' },
                { k: 'titlecase', l: 'Aa', title: 'Title Case' },
                { k: 'sentencecase', l: 'A.', title: 'Sentence case' },
              ] as const
            ).map((m) => (
              <button
                key={m.k}
                type="button"
                title={m.title}
                aria-label={m.title}
                aria-pressed={currentMods.has(m.k)}
                className={`dsg-tokenfmt-btn is-${m.k}${currentMods.has(m.k) ? ' is-on' : ''}`}
                onClick={() => toggleCase(m.k)}
              >
                {m.l}
              </button>
            ))}
          </span>
          {sep}
          <span className={`dsg-tokenfmt-color${color ? ' is-set' : ''}`}>
            <input
              type="color"
              value={`#${color || '000000'}`}
              onChange={(e) => setColor(e.target.value)}
              title="Text colour"
              aria-label="Text colour"
            />
            <span aria-hidden="true" style={color ? { color: `#${color}` } : undefined}>
              A
            </span>
          </span>
          {color && (
            <button
              type="button"
              className="dsg-tokenfmt-btn"
              title="Remove colour"
              aria-label="Remove colour"
              onClick={() => setColor(null)}
            >
              <X size={13} strokeWidth={2} aria-hidden />
            </button>
          )}
          {sizeAware && (
            <>
              {sep}
              <label
                className="dsg-tokenfmt-field"
                title="Font size override for this token, in points — leave blank to use the base size of the zone"
              >
                size
                <input
                  className="dsg-tokenfmt-input"
                  type="number"
                  min={4}
                  max={144}
                  step={1}
                  value={getTokenSize(value) ?? ''}
                  placeholder="—"
                  aria-label="Font size override, points"
                  onChange={(e) => {
                    const raw = e.target.value;
                    const pt = raw === '' ? null : parseFloat(raw) || null;
                    canvasSel.applyToSelection((v) => setTokenSize(v, pt));
                  }}
                />
                pt
              </label>
            </>
          )}
          {sep}
          <span className="dsg-tokenfmt-group-label">Spacing</span>
          {(['sb', 'sa'] as const).map((w) => (
            <label
              key={w}
              className="dsg-tokenfmt-field"
              title={w === 'sb' ? 'Space before the token (pt)' : 'Space after the token (pt)'}
            >
              {w === 'sb' ? 'before' : 'after'}
              <input
                className="dsg-tokenfmt-input"
                type="number"
                min={0}
                step={1}
                value={getSpace(currentMods, w) || ''}
                placeholder="0"
                aria-label={w === 'sb' ? 'Space before, points' : 'Space after, points'}
                onChange={(e) => {
                  const pt = parseFloat(e.target.value) || 0;
                  canvasSel.applyToSelection((v) => setTokenSpace(v, w, pt));
                }}
              />
            </label>
          ))}
          {clip && (
            <>
              {sep}
              {(
                [
                  {
                    l: 'Copy',
                    run: () => {
                      clip.copyKeys(clipTargetKeys());
                      canvasSel.select(myKey, { keepSelection: true });
                    },
                    on: true,
                  },
                  {
                    l: 'Cut',
                    run: () => {
                      clip.cutKeys(clipTargetKeys());
                      canvasSel.select(null);
                    },
                    on: true,
                  },
                  {
                    l: 'Paste',
                    run: () => {
                      clip.pasteAt(myKey);
                      canvasSel.select(myKey, { keepSelection: true });
                    },
                    on: clip.canPaste(),
                  },
                ] as const
              ).map((a) => (
                <button
                  key={a.l}
                  type="button"
                  className="dsg-tokenfmt-action"
                  onClick={a.run}
                  disabled={!a.on}
                >
                  {a.l}
                </button>
              ))}
            </>
          )}
          {onInsertLineBreak && (
            <>
              {sep}
              <button
                type="button"
                className="dsg-tokenfmt-action"
                title="Start a new line after this token"
                onClick={() => {
                  onInsertLineBreak();
                  canvasSel.select(null);
                }}
              >
                <CornerDownLeft size={12} strokeWidth={1.8} aria-hidden /> Line break
              </button>
            </>
          )}
          {onSetFixedDate && (
            <>
              {sep}
              <button
                type="button"
                className="dsg-tokenfmt-action"
                title="Replace this live date with a fixed date that never changes"
                onClick={() => {
                  onSetFixedDate();
                  canvasSel.select(null);
                }}
              >
                <CalendarDays size={12} strokeWidth={1.8} aria-hidden /> Fixed date…
              </button>
            </>
          )}
          {isCustom && onEdit && (
            <>
              {sep}
              <button
                type="button"
                className="dsg-tokenfmt-action"
                onClick={() => {
                  canvasSel.select(myKey);
                  onEdit();
                }}
              >
                <Pencil size={12} strokeWidth={1.8} aria-hidden /> Edit
              </button>
            </>
          )}
          {onRemove && (
            <>
              {sep}
              <button
                type="button"
                className="dsg-tokenfmt-action is-danger"
                onClick={() => {
                  onRemove();
                  canvasSel.select(null);
                }}
              >
                Delete
              </button>
            </>
          )}
        </div>
      );
    })(),
    document.body
  );
}

/* ─── Token Chip inside a DropZone ─── */
function ZoneChip({
  value,
  idx,
  zoneKey,
  onRemove,
  onEdit,
  onInsert,
  onReplace,
  fontSize,
  fontFamily,
  onUpdateValue,
  wysiwyg,
  sizeAware,
  special,
  images,
  doc,
}: {
  value: string;
  idx: number;
  zoneKey: string;
  onRemove?: () => void;
  onEdit?: () => void;
  onInsert?: (
    idx: number,
    value: string,
    sourceZone?: string | null,
    sourceIndex?: number | null,
    newLine?: boolean
  ) => void;
  onReplace?: (
    idx: number,
    value: string,
    sourceZone?: string | null,
    sourceIndex?: number | null
  ) => void;
  fontSize?: number;
  fontFamily?: string;
  onUpdateValue?: (idx: number, value: string) => void;
  // WYSIWYG mode: render the resolved value as live text instead of a label pill,
  // while keeping every interaction (drag-reorder, drop placement, click-edit, spacing grips).
  wysiwyg?: boolean;
  // Contract §3.3 fence: the shared chip renders bold/italic/caps/underline/
  // color regardless of caller (already-shipped, both surfaces show them).
  // Per-token SIZE is new functionality — the frozen classic canvas
  // (CanvasComponents.tsx) must NOT gain it, so it stays off unless the
  // caller opts in. The new Designer (DesignPalettes.tsx) sets it true.
  sizeAware?: boolean;
  special?: any;
  images?: Record<string, EmbeddedImage>;
  doc?: any;
}) {
  function baseToken(v: string) {
    const pi = v.lastIndexOf('|');
    if (pi <= 0) return v;
    let modStr = v.slice(pi + 1);
    if (modStr.endsWith('}')) modStr = modStr.slice(0, -1);
    const cands = modStr
      .toLowerCase()
      .split(',')
      .map((s) => s.trim());
    if (cands.every((m) => isValidMod(m))) return v.slice(0, pi) + (v.endsWith('}') ? '}' : '');
    return v;
  }

  const baseVal = baseToken(value);
  const isImage = baseVal.startsWith('{Image:');
  const isLine = baseVal === '{Line}';
  // {EndOfSection} isn't in ALL_TOKENS (it's a toggle-driven marker) but it IS a known
  // field that tokPreview resolves — so don't let it fall through to "custom literal".
  const isEos = baseVal === '{EndOfSection}';
  const isProjectToken = baseVal.startsWith('{ProjectToken:');
  // {P1}… aren't in ALL_TOKENS (they're minted per forma, not a fixed list),
  // so without this they'd read as literal typed text and the chip would show
  // a bare "{P1}" instead of resolving through tokPreview.
  const isPlaceholder = isNumberedPlaceholder(baseVal);
  const isCustom =
    !ALL_TOKENS.find((x) => x.value === baseVal) &&
    !isImage &&
    !isEos &&
    !isProjectToken &&
    !isPlaceholder &&
    value !== '\n';
  const isLineBreak = value === '\n';
  const label = tokLabel(value);
  const [hover, setHover] = tUseState<string | null>(null);
  const fmtRef = tUseRef<HTMLDivElement>(null);
  // The format menu (TokenFormatMenu, shared with the Studio's on-sheet
  // context menu) is portalled to <body> so it lands on top of everything
  // and isn't clipped/distorted by the zoomed canvas.
  // Single-selection model: this chip is "selected" / "menu open" only when the
  // canvas selection points at its key. Left-click selects; right-click opens
  // the context menu. Only one object anywhere can be selected/open at a time.
  const canvasSel = React.useContext(CanvasSelectionContext);
  const clip = React.useContext(TokenClipboardContext);
  const myKey = `token:${zoneKey}:${idx}`;
  const selected = canvasSel.selectedTokens.includes(myKey);
  const showFmt = canvasSel.menuKey === myKey;
  // Drag-to-space: grip the top/bottom edge to set space before/after live.
  const [chipHover, setChipHover] = tUseState(false);
  const [hoverGrip, setHoverGrip] = tUseState<null | 'sb' | 'sa'>(null);
  const [dragTip, setDragTip] = tUseState<null | {
    which: 'sb' | 'sa';
    val: number;
    x: number;
    y: number;
  }>(null);
  const chipFs = fontSize || 10;

  function parseMods(v: string) {
    const mods = new Set<string>();
    const pipeIdx = v.lastIndexOf('|');
    if (pipeIdx <= 0) return mods;
    let modStr = v.slice(pipeIdx + 1);
    if (modStr.endsWith('}')) modStr = modStr.slice(0, -1);
    const cands = modStr
      .toLowerCase()
      .split(',')
      .map((s) => s.trim());
    if (cands.every((m) => isValidMod(m))) cands.forEach((m) => mods.add(m));
    return mods;
  }

  function stripBase(v: string): string {
    const pipeIdx = v.lastIndexOf('|');
    if (pipeIdx <= 0) return v;
    let testStr = v.slice(pipeIdx + 1);
    if (testStr.endsWith('}')) testStr = testStr.slice(0, -1);
    if (
      testStr
        .toLowerCase()
        .split(',')
        .map((s) => s.trim())
        .every((m) => isValidMod(m))
    ) {
      return v.slice(0, pipeIdx) + (v.endsWith('}') ? '}' : '');
    }
    return v;
  }

  function rebuildToken(base: string, mods: Set<string>) {
    if (mods.size === 0) {
      if (onUpdateValue) onUpdateValue(idx, base);
    } else {
      const modStr = [...mods].join(',');
      if (base.endsWith('}')) {
        if (onUpdateValue) onUpdateValue(idx, base.slice(0, -1) + '|' + modStr + '}');
      } else {
        if (onUpdateValue) onUpdateValue(idx, base + '|' + modStr);
      }
    }
  }

  // Vertical spacing (pt) before/after the token — sb:/sa: mods. (Bold/italic/
  // caps/underline/color toggles live in TokenFormatMenu now — the shared
  // popover both this chip and the Studio's on-sheet menu render.)
  function setSpace(which: 'sb' | 'sa', pt: number) {
    const mods = parseMods(value);
    for (const m of [...mods]) if (m.startsWith(which + ':')) mods.delete(m);
    if (pt > 0) mods.add(which + ':' + pt);
    rebuildToken(stripBase(value), mods);
  }
  function getSpace(mods: Set<string>, which: 'sb' | 'sa'): number {
    for (const m of mods) if (m.startsWith(which + ':')) return parseFloat(m.slice(3)) || 0;
    return 0;
  }
  // Grab an edge and drag — 2px ≈ 1pt — with a live readout. Forma should feel
  // like nudging the layout, not typing into a box.
  function startSpacingDrag(which: 'sb' | 'sa', e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const startVal = getSpace(parseMods(value), which);
    setDragTip({ which, val: startVal, x: e.clientX, y: e.clientY });
    const onMove = (ev: MouseEvent) => {
      const dyPx = which === 'sb' ? startY - ev.clientY : ev.clientY - startY;
      const val = Math.max(0, Math.min(96, Math.round(startVal + dyPx * 0.5)));
      setSpace(which, val);
      setDragTip({ which, val, x: ev.clientX, y: ev.clientY });
    };
    const onUp = () => {
      setDragTip(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  const currentMods = parseMods(value);

  function getZone(e: React.DragEvent, rect: DOMRect) {
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x > 0.3 && x < 0.7 && y > 0.25 && y < 0.75) return 'center';
    const dists: Record<string, number> = { top: y, bottom: 1 - y, left: x, right: 1 - x };
    return Object.entries(dists).sort((a, b) => a[1] - b[1])[0][0];
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setHover(getZone(e, rect));
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    const zone = hover;
    setHover(null);
    try {
      const d = JSON.parse(e.dataTransfer.getData('application/json'));
      // A whole multi-selection, or a Ctrl-drag copy → route through moveTokens.
      if (d.type === 'tokenMulti') {
        canvasSel.moveTokens(zoneKey, d.keys, !!d.copy);
        return;
      }
      if (d.type === 'token' && d.copy && d.sourceZone != null) {
        canvasSel.moveTokens(zoneKey, [`token:${d.sourceZone}:${d.sourceIndex}`], true);
        return;
      }
      if (zone === 'center') {
        if (onReplace) onReplace(idx, d.value, d.sourceZone, d.sourceIndex);
      } else {
        const insertAfter = zone === 'bottom' || zone === 'right';
        const newLine = zone === 'top' || zone === 'bottom';
        if (onInsert)
          onInsert(insertAfter ? idx + 1 : idx, d.value, d.sourceZone, d.sourceIndex, newLine);
      }
    } catch (err) {
      /* ignore */
    }
  }

  const renderLabel = () => {
    if (isCustom) {
      const raw = value;
      const lines = raw.split('\n');
      return lines.map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 && <br />}
          {`"${line}"`}
        </React.Fragment>
      ));
    }
    return label;
  };

  const indStyle = (pos: string): React.CSSProperties => ({
    position: 'absolute',
    ...(pos === 'top'
      ? {
          top: -2,
          left: 0,
          right: 0,
          height: 3,
          background: 'var(--ac)',
          boxShadow: '0 0 6px rgba(var(--acr), .55)',
        }
      : {}),
    ...(pos === 'bottom'
      ? {
          bottom: -2,
          left: 0,
          right: 0,
          height: 3,
          background: 'var(--ac)',
          boxShadow: '0 0 6px rgba(var(--acr), .55)',
        }
      : {}),
    ...(pos === 'left'
      ? {
          left: -3,
          top: 0,
          bottom: 0,
          width: 3,
          background: 'var(--ac)',
          boxShadow: '0 0 6px rgba(var(--acr), .55)',
        }
      : {}),
    ...(pos === 'right'
      ? {
          right: -3,
          top: 0,
          bottom: 0,
          width: 3,
          background: 'var(--ac)',
          boxShadow: '0 0 6px rgba(var(--acr), .55)',
        }
      : {}),
    borderRadius: 2,
    pointerEvents: 'none',
    zIndex: 10,
  });

  // ── WYSIWYG resolution: what this token actually renders to on the page ──
  const wBase = tokBase(value);
  const wIsLine = wBase === '{Line}';
  const wIsImage = wBase.startsWith('{Image:');
  const wIsField = !isCustom && !isLineBreak && !wIsLine && !wIsImage;
  const wIsProjectTok =
    /^\{(ProjectToken:|IssuanceDate\}|(Project|Client|Issuance|Team|Deliverable)[.}])/.test(
      wBase
    ) || isNumberedPlaceholder(wBase);
  // A placeholder carries its own story: what the imported template had here,
  // and — in a project copy — whether anyone has said what it means yet.
  const phId = placeholderId(wBase);
  const phMemory = phId ? placeholderMemory(phId) : undefined;
  const phResolved = phId ? placeholderValue(phId) : undefined;
  const phUnbound = !!phId && isProjectForma() && !phResolved;
  const phTip = phId
    ? [
        `${phId === 'IssuanceDate' ? 'Issuance date' : 'Placeholder ' + phId} — ` +
          (phResolved
            ? `prints “${phResolved}”.`
            : isProjectForma()
              ? 'not set for this project yet — set it in Project Tokens.'
              : 'the project decides what this says.'),
        phMemory ? `The template had “${phMemory}” here.` : null,
      ]
        .filter(Boolean)
        .join(' ')
    : null;
  const wModStyle = tokModStyle(value);
  if (!sizeAware && wModStyle.fontSize) delete wModStyle.fontSize;
  function renderResolved(): React.ReactNode {
    if (isLineBreak) return <span>↵</span>;
    if (wIsLine) {
      const lp = parseLineParams(value);
      return (
        <span
          style={{
            display: 'inline-block',
            width: 44,
            borderBottom: `${lp.thickness}px solid #${lp.color}`,
            verticalAlign: 'middle',
          }}
        />
      );
    }
    if (wIsImage) {
      const imgId = parseImageId(value);
      const img = imgId && images ? images[imgId] : null;
      if (img) {
        const maxH = 30;
        const scale = maxH / img.height;
        return (
          <img
            src={`data:${img.mimeType};base64,${img.data}`}
            style={{ height: maxH, width: Math.round(img.width * scale), objectFit: 'contain' }}
          />
        );
      }
      return <span style={{ fontStyle: 'italic', opacity: 0.5 }}>[image]</span>;
    }
    if (isCustom) {
      // Literal typed text: render the actual words — NOT the edit-mode quotes and NOT
      // the "|bold,caps" suffix (that's applied as style via wModStyle on the span).
      // titlecase/sentencecase need the real string transform (CSS has no
      // sentence-case, and `capitalize` doesn't lowercase already-caps text).
      return applyCaseModText(baseVal, value)
        .split('\n')
        .map((ln, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {ln}
          </React.Fragment>
        ));
    }
    if (baseVal === '{Space}') {
      // A space is real content but paints nothing — give it a quiet visible
      // body (paper-relative neutrals; the paper is always white) so it can be
      // seen, hovered, and selected instead of being a 3px ghost.
      return (
        <span
          aria-label="Space"
          style={{
            display: 'inline-block',
            width: '0.6em',
            minWidth: 7,
            height: '1.05em',
            borderRadius: 2,
            background: chipHover || selected ? 'rgba(var(--acr),.22)' : 'rgba(120,130,145,.14)',
            boxShadow: 'inset 0 0 0 1px rgba(120,130,145,.4)',
            verticalAlign: 'text-bottom',
          }}
        />
      );
    }
    const pRaw = tokPreview(value, special, doc);
    const p = pRaw ? applyCaseModText(pRaw, value) : pRaw;
    if (isProjectToken && p && p.includes('\n')) {
      // Resolved project-token content can be multiline — render real breaks.
      return p.split('\n').map((ln, i) => (
        <React.Fragment key={i}>
          {i > 0 && <br />}
          {ln}
        </React.Fragment>
      ));
    }
    return p || label;
  }

  return (
    <Hint text={phTip}>
      <div
        ref={fmtRef}
        data-token-key={myKey}
        style={{
          position: 'relative',
          display: 'inline-flex',
          borderRadius: 4,
          outline: selected ? '2px solid var(--ac)' : undefined,
          outlineOffset: 1,
          background: selected ? 'rgba(var(--acr),.16)' : undefined,
        }}
        onDragOver={handleDragOver}
        onDragLeave={() => setHover(null)}
        onDrop={handleDrop}
        onMouseEnter={() => setChipHover(true)}
        onMouseLeave={() => setChipHover(false)}
      >
        {hover && hover !== 'center' && <div style={indStyle(hover)} />}
        {hover === 'center' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(220,50,50,0.15)',
              border: '2px solid rgba(220,50,50,0.5)',
              borderRadius: 4,
              pointerEvents: 'none',
              zIndex: 10,
            }}
          />
        )}
        <span
          draggable={!dragTip}
          onDragStart={(e) => {
            const copy = e.ctrlKey || e.metaKey;
            const isMulti = selected && canvasSel.selectedTokens.length > 1;
            if (isMulti) {
              e.dataTransfer.setData(
                'application/json',
                JSON.stringify({ type: 'tokenMulti', keys: canvasSel.selectedTokens, copy })
              );
            } else {
              e.dataTransfer.setData(
                'application/json',
                JSON.stringify({
                  type: 'token',
                  value,
                  sourceZone: zoneKey,
                  sourceIndex: idx,
                  copy,
                })
              );
            }
            e.dataTransfer.effectAllowed = copy ? 'copy' : 'copyMove';
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (isLineBreak) return;
            // Left-click = select. Ctrl/Cmd-click = add/remove from a multi-selection.
            canvasSel.select(myKey, { additive: e.ctrlKey || e.metaKey });
          }}
          onContextMenu={(e) => {
            if (isLineBreak) return;
            // Right-click opens the context menu. If this token is part of a
            // multi-selection, keep the whole selection so the menu acts on all
            // of them; otherwise select just this one.
            e.preventDefault();
            e.stopPropagation();
            const inMulti =
              canvasSel.selectedTokens.includes(myKey) && canvasSel.selectedTokens.length > 1;
            canvasSel.select(myKey, {
              menu: true,
              keepSelection: inMulti,
              x: e.clientX,
              y: e.clientY,
            });
          }}
          onDoubleClick={(e) => {
            // A custom text token opens its editor on double-click — same as the
            // Edit button in the format menu.
            if (isLineBreak || !isCustom || !onEdit) return;
            e.preventDefault();
            e.stopPropagation();
            canvasSel.select(myKey);
            onEdit();
          }}
          style={
            wysiwyg
              ? isLineBreak
                ? {
                    // A return is a structural marker, not content — a clearly visible,
                    // grabbable badge (big enough to actually click).
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 20,
                    height: 20,
                    fontSize: Math.max(13, chipFs + 2),
                    lineHeight: 1,
                    padding: '0 5px',
                    borderRadius: 4,
                    background: chipHover ? 'rgba(var(--acr),.16)' : 'rgba(120,130,145,.12)',
                    border: `1px solid ${chipHover ? 'rgba(var(--acr),.5)' : 'rgba(120,130,145,.4)'}`,
                    color: chipHover ? 'var(--ac)' : 'var(--tm)',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }
                : {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 2,
                    padding: '0 1px',
                    fontSize: chipFs,
                    fontFamily: 'inherit',
                    lineHeight: 1.3,
                    // Data fields get a faint dotted underline (a Word-field cue); literal text
                    // you typed stays plain. Hovering lifts a soft tint so it reads as editable.
                    // A placeholder is a BLANK, so it reads as one: a dashed
                    // underline instead of the field's dotted one. In a project
                    // copy an unbound blank is a problem, so it also carries a
                    // warning wash — not color alone (DESIGN.md §7): the dashes
                    // and the "not set" tooltip say it too.
                    // --color-warning is only 2.2:1 on the light paper, below the
                    // 3:1 floor for a control boundary (§7) — danger clears it in
                    // both themes and is the honest severity anyway: an unbound
                    // blank BLOCKS the export.
                    borderBottom: phId
                      ? `1px dashed ${phUnbound ? 'var(--color-danger)' : 'rgba(var(--acr),.55)'}`
                      : wIsField
                        ? `1px dotted ${chipHover ? 'rgba(var(--acr),.7)' : 'rgba(150,160,175,.5)'}`
                        : '1px dotted transparent',
                    background: phUnbound
                      ? 'color-mix(in srgb, var(--color-warning) 16%, transparent)'
                      : chipHover
                        ? 'rgba(var(--acr),.08)'
                        : 'transparent',
                    borderRadius: 3,
                    color: 'inherit',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontStyle: wIsProjectTok ? 'italic' : undefined,
                    opacity: wIsProjectTok ? 0.8 : 1,
                    ...wModStyle,
                  }
              : {
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontSize: chipFs,
                  fontWeight: isCustom ? 500 : 550,
                  // Mono = literal text you typed; sans = a data field. The paper is
                  // always white, so these neutrals are paper-relative, not theme vars.
                  fontFamily: isCustom
                    ? "'JetBrains Mono',monospace"
                    : "'DM Sans',system-ui,sans-serif",
                  background: isLineBreak ? 'transparent' : 'var(--forma-chip-bg)',
                  color: isLineBreak ? 'var(--forma-chip-line-text)' : 'var(--forma-chip-text)',
                  border: `1px ${isLineBreak ? 'dashed' : 'solid'} ${
                    isLineBreak ? 'var(--forma-chip-line)' : 'var(--forma-chip-border)'
                  }`,
                  cursor: 'pointer',
                  userSelect: 'none',
                }
          }
        >
          <span>{wysiwyg ? renderResolved() : renderLabel()}</span>
          {!wysiwyg && isCustom && onEdit && (
            <Pencil
              size={11}
              strokeWidth={1.8}
              style={{ opacity: 0.5, flexShrink: 0 }}
              aria-hidden
            />
          )}
          {onRemove && (!wysiwyg || chipHover || selected) && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Remove token"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onRemove();
                }
              }}
              title="Remove token"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--ac)';
                e.currentTarget.style.color = 'var(--color-text-inverse)';
                e.currentTarget.style.transform = wysiwyg ? 'scale(1.1)' : 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = wysiwyg
                  ? 'var(--forma-chip-bg, rgba(240,242,246,.95))'
                  : 'rgba(var(--acr),.12)';
                e.currentTarget.style.color = 'var(--ac)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              style={
                wysiwyg
                  ? {
                      // WYSIWYG: the × floats OUTSIDE flow (absolute, corner badge).
                      // Mounting it on hover must never change the chip's width —
                      // an inline × shifted centered zones under the cursor and
                      // caused the hover↔unhover render oscillation.
                      position: 'absolute',
                      top: -9,
                      right: -9,
                      zIndex: 14,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 18,
                      height: 18,
                      borderRadius: 9999,
                      fontSize: 11,
                      lineHeight: 1,
                      cursor: 'pointer',
                      color: 'var(--ac)',
                      background: 'var(--forma-chip-bg, rgba(240,242,246,.95))',
                      border: '1px solid rgba(var(--acr),.4)',
                      boxShadow: '0 1px 3px rgba(0,0,0,.18)',
                      transition: 'background .12s, color .12s, transform .12s',
                    }
                  : {
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 16,
                      height: 16,
                      marginLeft: 3,
                      flexShrink: 0,
                      borderRadius: 4,
                      fontSize: 13,
                      lineHeight: 1,
                      cursor: 'pointer',
                      color: 'var(--ac)',
                      background: 'rgba(var(--acr),.12)',
                      transition: 'background .12s, color .12s, transform .12s',
                    }
              }
            >
              ×
            </span>
          )}
        </span>
        {/* Per-token vertical spacing — the upper token's Space After and the lower
          token's Space Before are DISTINCT: each renders as its own tinted band with
          the drag-bar at the far edge (where the space ends), same model as the levels. */}
        {!isLineBreak &&
          (['sb', 'sa'] as const).map((which) => {
            const isTop = which === 'sb';
            const val = getSpace(currentMods, which);
            const active = val > 0;
            const lit = hoverGrip === which || dragTip?.which === which;
            // Spacing bars: contextual (hover OR selected), interactive in both —
            // you can see AND drag spacing the moment you hover a token; never
            // persistent just because a value exists. The bars are absolutely
            // positioned, so showing them never reflows the chip (no flicker).
            if (!selected && !chipHover) return null;
            const interactive = selected || chipHover;
            const gapPx = ptPx(val);
            return (
              <React.Fragment key={which}>
                {/* the band of space this token owns on this edge */}
                {(active || lit) && gapPx > 1 && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      ...(isTop ? { bottom: '100%' } : { top: '100%' }),
                      height: gapPx,
                      background: lit ? 'rgba(var(--acr),.13)' : 'rgba(var(--acr),.07)',
                      borderRadius: 2,
                      pointerEvents: 'none',
                      zIndex: 11,
                    }}
                  />
                )}
                {/* the draggable bar — sits where the space ends (far edge of the band).
                  Interactable only when selected; a faint hint when merely hovering. */}
                <div
                  title={
                    interactive ? (isTop ? 'Space Before — drag' : 'Space After — drag') : undefined
                  }
                  onMouseDown={interactive ? (e) => startSpacingDrag(which, e) : undefined}
                  onMouseEnter={interactive ? () => setHoverGrip(which) : undefined}
                  onMouseLeave={interactive ? () => setHoverGrip(null) : undefined}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    ...(isTop ? { top: -(gapPx + 4) } : { bottom: -(gapPx + 4) }),
                    height: 8,
                    cursor: interactive ? 'ns-resize' : 'default',
                    pointerEvents: interactive ? 'auto' : 'none',
                    opacity: interactive ? 1 : 0.4,
                    zIndex: 12,
                    display: 'flex',
                    alignItems: 'center',
                    transition: dragTip ? 'none' : 'top .1s, bottom .1s',
                  }}
                >
                  <div
                    style={{
                      height: lit ? 4 : active ? 3 : 2,
                      width: '100%',
                      borderRadius: 2,
                      background: lit
                        ? 'var(--ac)'
                        : active
                          ? 'rgba(var(--acr),.55)'
                          : 'rgba(var(--acr),.28)',
                    }}
                  />
                  {lit && !dragTip && (
                    <div
                      style={{
                        position: 'absolute',
                        ...(isTop ? { bottom: 11 } : { top: 11 }),
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'var(--ac)',
                        color: 'var(--color-text-inverse)',
                        fontSize: 9,
                        fontWeight: 700,
                        padding: '1px 5px',
                        borderRadius: 3,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                      }}
                    >
                      {isTop ? 'Space Before' : 'Space After'}
                      {active ? ` · ${val} pt` : ''}
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        {/* super-subtle field tag — what this token resolves from (data fields only, on hover) */}
        {wysiwyg && wIsField && chipHover && !hoverGrip && !dragTip && (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              marginBottom: 3,
              background: 'rgba(55,60,68,.9)',
              color: '#fff',
              fontSize: 8.5,
              fontWeight: 600,
              letterSpacing: '.02em',
              padding: '1px 5px',
              borderRadius: 3,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              zIndex: 13,
            }}
          >
            {label}
          </div>
        )}
        {dragTip &&
          createPortal(
            // Portal to body so the readout isn't distorted by the canvas zoom transform.
            <div
              style={{
                position: 'fixed',
                left: dragTip.x + 14,
                top: dragTip.y - 10,
                zIndex: 100000,
                background: 'var(--ac)',
                color: 'var(--color-text-inverse)',
                fontSize: 11,
                fontWeight: 700,
                padding: '3px 8px',
                borderRadius: 5,
                boxShadow: '0 2px 8px rgba(0,0,0,.25)',
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {dragTip.which === 'sb' ? 'Space before' : 'Space after'} · {dragTip.val} pt
            </div>,
            document.body
          )}
        {showFmt && !isLineBreak && (
          <TokenFormatMenu
            value={value}
            myKey={myKey}
            isCustom={isCustom}
            sizeAware={sizeAware}
            onEdit={isCustom && onEdit ? onEdit : undefined}
            onRemove={onRemove}
            anchorRef={fmtRef}
          />
        )}
      </div>
    </Hint>
  );
}

/* ─── Drop Zone on Canvas ─── */
function DropZone({
  zoneKey,
  position,
  tokens,
  onDrop,
  onRemove,
  onEditToken,
  onInsertAt,
  onReplaceAt,
  onUpdateToken,
  visible,
  ghost,
  style,
  onCustomDrop,
  fontSize,
  sizeAware,
  special,
  fontFamily,
  images,
  doc,
}: {
  zoneKey: string;
  position: string;
  tokens: string[];
  // sourceZone/Index accept both `null` and `undefined` as "no source" — the Canvas caller
  // uses null, the internal HTML5-drag path uses undefined. Either way means the same thing.
  onDrop: (value: string, sourceZone?: string | null, sourceIndex?: number | null) => void;
  onRemove: (idx: number) => void;
  onEditToken?: (idx: number) => void;
  onInsertAt?: (
    idx: number,
    value: string,
    sourceZone?: string | null,
    sourceIndex?: number | null,
    newLine?: boolean
  ) => void;
  onReplaceAt?: (
    idx: number,
    value: string,
    sourceZone?: string | null,
    sourceIndex?: number | null
  ) => void;
  onUpdateToken?: (idx: number, value: string) => void;
  visible?: boolean;
  // Ghost = mounted + drop-live but painted invisible (empty idle slot). The
  // slot must ALWAYS exist in the layout: conditionally mounting it grew the
  // zone under the cursor mid-drag and re-fired dragleave — the drop-target
  // flicker loop.
  ghost?: boolean;
  style?: React.CSSProperties;
  onCustomDrop?: (sourceZone?: string | null, sourceIndex?: number | null) => void;
  fontSize?: number;
  /** See ZoneChip's sizeAware doc — passed straight through. */
  sizeAware?: boolean;
  special?: any;
  fontFamily?: string;
  images?: Record<string, EmbeddedImage>;
  doc?: any;
}) {
  const [over, setOver] = tUseState(false);
  const dzSel = React.useContext(CanvasSelectionContext);
  const dzClip = React.useContext(TokenClipboardContext);
  const slotKey = `slot:${zoneKey}`;
  const slotSelected = dzSel.selKey === slotKey;
  // Right-click on slot whitespace → clipboard context menu (copy/cut/paste
  // triad, DESIGN.md §4). The menu itself is the shared TokenSlotMenu, so the
  // Studio's on-sheet column regions offer exactly these actions.
  const [slotMenu, setSlotMenu] = tUseState<{ x: number; y: number } | null>(null);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setOver(false);
    try {
      const d = JSON.parse(e.dataTransfer.getData('application/json'));
      // A whole multi-selection, or a Ctrl-drag copy → route through moveTokens.
      if (d.type === 'tokenMulti') {
        dzSel.moveTokens(zoneKey, d.keys, !!d.copy);
        return;
      }
      if (d.type === 'token' && d.copy && d.sourceZone != null) {
        dzSel.moveTokens(zoneKey, [`token:${d.sourceZone}:${d.sourceIndex}`], true);
        return;
      }
      if (d.value === '__custom__') {
        if (onCustomDrop) onCustomDrop(d.sourceZone, d.sourceIndex);
      } else onDrop(d.value, d.sourceZone, d.sourceIndex);
    } catch (err) {
      /* ignore */
    }
  }

  const has = tokens && tokens.length > 0;
  // Always-WYSIWYG: zones render their resolved tokens inline as live, interactive text
  // (no hover-to-edit mode flip).
  const jc = position === 'left' ? 'flex-start' : position === 'right' ? 'flex-end' : 'center';
  const ta = position === 'right' ? 'right' : position === 'center' ? 'center' : 'left';

  // Group tokens into visual lines (split at \n), keeping \n as a chip at end of its line
  const lines: Array<Array<{ token: string; idx: number }>> = [[]];
  if (has) {
    tokens.forEach((t, i) => {
      if (t === '\n') {
        // Add the \n chip to the CURRENT line so it's visible & removable
        lines[lines.length - 1].push({ token: t, idx: i });
        lines.push([]);
      } else {
        lines[lines.length - 1].push({ token: t, idx: i });
      }
    });
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false);
      }}
      onDrop={handleDrop}
      onClick={(e) => {
        // Click an empty slot area → select the SLOT (paste target, ring).
        // Chip clicks stopPropagation, so this only fires on slot whitespace.
        e.stopPropagation();
        dzSel.select(slotKey);
      }}
      onContextMenu={(e) => {
        // Right-click slot whitespace → clipboard menu. Chip right-clicks
        // stopPropagation, so this fires only for the slot itself.
        if (!dzClip) return;
        e.preventDefault();
        e.stopPropagation();
        dzSel.select(slotKey);
        setSlotMenu({ x: e.clientX, y: e.clientY });
      }}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        minHeight: 26,
        padding: '3px 5px',
        borderRadius: 4,
        transition: 'border-color 0.2s, background 0.2s',
        border: over
          ? '1.5px dashed var(--ac)'
          : slotSelected
            ? '1.5px solid var(--ac)'
            : (visible || !ghost) && !has
              ? '1.5px dashed rgba(150,150,150,0.22)'
              : '1.5px dashed transparent',
        background: over
          ? 'rgba(var(--acr),0.08)'
          : slotSelected
            ? 'rgba(var(--acr),0.05)'
            : 'transparent',
        ...style,
      }}
    >
      {has
        ? lines.map((line, li) => {
            // Token Space Before / Space After renders as a real gap (max across the line),
            // so the zone reads exactly like the page will — spacing is no longer invisible.
            const lineSb = line.length
              ? Math.max(0, ...line.map((x) => tokSpacing(x.token).before))
              : 0;
            const lineSa = line.length
              ? Math.max(0, ...line.map((x) => tokSpacing(x.token).after))
              : 0;
            return (
              <div
                key={li}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 3,
                  alignItems: 'center',
                  justifyContent:
                    jc === 'flex-end' ? 'flex-end' : jc === 'center' ? 'center' : 'flex-start',
                  minHeight: 18,
                  marginTop: ptPx(lineSb),
                  marginBottom: ptPx(lineSa),
                }}
              >
                {line.length > 0 ? (
                  line.map(({ token, idx }) => (
                    <ZoneChip
                      key={idx}
                      value={token}
                      idx={idx}
                      zoneKey={zoneKey}
                      onRemove={() => onRemove(idx)}
                      onEdit={onEditToken ? () => onEditToken(idx) : undefined}
                      onInsert={onInsertAt}
                      onReplace={onReplaceAt}
                      onUpdateValue={onUpdateToken}
                      fontSize={fontSize}
                      wysiwyg
                      sizeAware={sizeAware}
                      special={special}
                      images={images}
                      doc={doc}
                      fontFamily={fontFamily}
                    />
                  ))
                ) : (
                  <span
                    style={{ fontSize: 11, color: 'rgba(150,150,150,0.35)', fontStyle: 'italic' }}
                  >
                    &nbsp;
                  </span>
                )}
              </div>
            );
          })
        : visible && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent:
                  jc === 'flex-end' ? 'flex-end' : jc === 'center' ? 'center' : 'flex-start',
                minHeight: 20,
              }}
            >
              <span
                style={{
                  fontSize: fontSize || 9,
                  color: 'rgba(150,150,150,0.45)',
                  fontStyle: 'italic',
                }}
              >
                {position}
              </span>
            </div>
          )}
      {slotMenu && dzClip && (
        <TokenSlotMenu
          at={slotMenu}
          label={`${position} column actions`}
          items={slotClipboardItems(dzClip, zoneKey, has)}
          onClose={() => setSlotMenu(null)}
        />
      )}
    </div>
  );
}

/* ─── Dock furniture — quiet group labels and hairline rules ─── */
function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: 'var(--color-text-subtle, var(--tm))',
        textTransform: 'uppercase',
        letterSpacing: '.09em',
        userSelect: 'none',
        marginRight: 2,
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );
}

function DockRule() {
  return (
    <span
      style={{
        width: 1,
        height: 18,
        background: 'var(--bd)',
        margin: '0 8px',
        flexShrink: 0,
      }}
    />
  );
}

/* ─── Quiet text/icon button used by the zoom cluster ─── */
function DockBtn({
  children,
  onClick,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title?: string;
}) {
  const [hover, setHover] = tUseState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        height: 22,
        minWidth: 22,
        padding: '0 6px',
        border: 'none',
        borderRadius: 5,
        background: hover ? 'var(--color-hover, var(--sa))' : 'transparent',
        color: hover ? 'var(--tx)' : 'var(--tm)',
        fontSize: 11,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        lineHeight: 1,
        transition: 'background .12s ease, color .12s ease',
      }}
    >
      {children}
    </button>
  );
}

/* ─── "New text" chip — drag out and type a literal ─── */
function NewCustomChip({ onStart, onEnd }: { onStart?: () => void; onEnd?: () => void }) {
  const [hover, setHover] = tUseState(false);
  const [grabbing, setGrabbing] = tUseState(false);
  return (
    <span
      draggable
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onDragStart={(e) => {
        e.dataTransfer.setData(
          'application/json',
          JSON.stringify({ type: 'token', value: '__custom__' })
        );
        e.dataTransfer.effectAllowed = 'copy';
        setTokenDragGhost(e, 'New text');
        setGrabbing(true);
        if (onStart) onStart();
      }}
      onDragEnd={() => {
        setGrabbing(false);
        if (onEnd) onEnd();
      }}
      title="Drag onto the page, then type your text"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        height: 26,
        padding: '0 10px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 500,
        background: grabbing
          ? 'var(--ach)'
          : hover
            ? 'var(--color-hover, var(--sa))'
            : 'transparent',
        color: 'var(--tm)',
        border: `1px dashed ${grabbing ? 'var(--ac)' : 'var(--color-border-strong, var(--bd))'}`,
        cursor: grabbing ? 'grabbing' : 'grab',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        transform: hover && !grabbing ? 'translateY(-1px)' : 'none',
        boxShadow: hover && !grabbing ? 'var(--shadow-sm)' : 'none',
        transition:
          'transform .12s ease, box-shadow .12s ease, background .12s ease, border-color .12s ease',
      }}
    >
      <span style={{ fontSize: 14, lineHeight: 1, marginTop: -1 }}>+</span>
      Text
    </span>
  );
}

/* ─── Custom token chip (saved / recent) — mono + quotes = literal text ─── */
function CustomChip({
  value,
  saved,
  onPin,
  onRemove,
  onStart,
  onEnd,
}: {
  value: string;
  saved?: boolean;
  onPin?: () => void;
  onRemove?: () => void;
  onStart?: () => void;
  onEnd?: () => void;
}) {
  const [hover, setHover] = tUseState(false);
  const [grabbing, setGrabbing] = tUseState(false);
  const flat = value.replace(/\n/g, ' ↵ ');
  const text = flat.length > 28 ? flat.slice(0, 28) + '…' : flat;
  return (
    <span
      draggable
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onDragStart={(e) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ type: 'token', value }));
        e.dataTransfer.effectAllowed = 'copy';
        setTokenDragGhost(e, `"${text}"`, true);
        setGrabbing(true);
        if (onStart) onStart();
      }}
      onDragEnd={() => {
        setGrabbing(false);
        if (onEnd) onEnd();
      }}
      title={value}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 26,
        padding: '0 9px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 500,
        fontFamily: 'var(--font-mono)',
        background: grabbing ? 'var(--ach)' : hover ? 'var(--color-hover, var(--sa))' : 'var(--sa)',
        color: saved ? 'var(--tx)' : 'var(--tm)',
        border: `1px solid ${grabbing ? 'var(--ac)' : 'var(--bd)'}`,
        cursor: grabbing ? 'grabbing' : 'grab',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        maxWidth: 240,
        transform: hover && !grabbing ? 'translateY(-1px)' : 'none',
        boxShadow: hover && !grabbing ? 'var(--shadow-sm)' : 'none',
        transition:
          'transform .12s ease, box-shadow .12s ease, background .12s ease, border-color .12s ease',
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>"{text}"</span>
      {saved && onRemove && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onRemove();
          }}
          style={{
            cursor: 'pointer',
            opacity: hover ? 0.7 : 0.25,
            flexShrink: 0,
            fontSize: 12,
            lineHeight: 1,
            transition: 'opacity .12s',
          }}
          title="Remove from saved"
        >
          ×
        </span>
      )}
      {!saved && onPin && (
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onPin();
          }}
          style={{
            cursor: 'pointer',
            opacity: hover ? 0.7 : 0.25,
            flexShrink: 0,
            transition: 'opacity .12s',
          }}
        >
          <title>Pin to saved</title>
          <path
            d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z"
            fill="currentColor"
          />
        </svg>
      )}
    </span>
  );
}

/* ─── Zoom cluster — one bordered group; accent lives only in the slider ─── */
function ZoomCluster({
  zoom,
  onZoomChange,
  onFit,
}: {
  zoom: number;
  onZoomChange: (z: number) => void;
  onFit: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        height: 28,
        padding: '0 5px',
        border: '1px solid var(--bd)',
        borderRadius: 7,
        background: 'var(--sf)',
        flexShrink: 0,
      }}
    >
      <DockBtn
        title="Zoom out"
        onClick={() => onZoomChange(Math.max(0.3, +(zoom - 0.1).toFixed(1)))}
      >
        −
      </DockBtn>
      <input
        type="range"
        min="0.3"
        max="2"
        step="0.05"
        value={zoom}
        onChange={(e) => onZoomChange(+e.target.value)}
        style={{ width: 84, accentColor: 'var(--ac)', cursor: 'pointer' }}
      />
      <DockBtn title="Zoom in" onClick={() => onZoomChange(Math.min(2, +(zoom + 0.1).toFixed(1)))}>
        +
      </DockBtn>
      <span
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: 'var(--tm)',
          minWidth: 38,
          textAlign: 'center',
          fontVariantNumeric: 'tabular-nums',
          userSelect: 'none',
        }}
      >
        {Math.round(zoom * 100)}%
      </span>
      <span
        style={{ width: 1, height: 14, background: 'var(--bd)', margin: '0 3px', flexShrink: 0 }}
      />
      <DockBtn title="Fit page to window" onClick={onFit}>
        Fit
      </DockBtn>
      <DockBtn title="Reset zoom" onClick={() => onZoomChange(1)}>
        100%
      </DockBtn>
    </div>
  );
}

/* ─── Rail section — a labeled group of chips stacked in the right rail ─── */
function RailSection({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <Hint text={hint}>
        <span tabIndex={hint ? 0 : undefined} style={{ alignSelf: 'flex-start' }}>
          <GroupLabel>{label}</GroupLabel>
        </span>
      </Hint>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{children}</div>
    </div>
  );
}

/* ─── Token Rail — vertical token palette docked on the editor's right.
       Same calm chips as the old top dock, stacked into labeled sections;
       the accent is still reserved for drag + drop targets. ─── */
function TokenRail({
  savedTokens,
  recentTokens,
  onSave,
  onRemoveSaved,
  onStart,
  onEnd,
  onInsert,
}: {
  savedTokens: string[];
  recentTokens: string[];
  onSave: (t: string) => void;
  onRemoveSaved: (t: string) => void;
  onStart?: () => void;
  onEnd?: () => void;
  /** Optional click-to-insert (Designer). Absent → drag-only, as before. */
  onInsert?: (value: string) => void;
}) {
  const ELEMENT_IDS = ['Space', 'Return', 'Line', 'Image'];
  const fieldTokens = TOKENS.filter((t) => !ELEMENT_IDS.includes(t.id));
  const elemTokens = ELEMENT_IDS.map((id) => TOKENS.find((t) => t.id === id)).filter(
    (t): t is (typeof TOKENS)[number] => !!t
  );
  const savedSet = new Set(savedTokens);
  const recentOnly = recentTokens.filter((t) => !savedSet.has(t)).slice(0, 4);

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '12px 12px 24px',
      }}
    >
      <RailSection label="Fields" hint={FORMA_TIPS.tokenFields}>
        {fieldTokens.map((t) => (
          <PaletteChip
            key={t.id}
            value={t.value}
            label={t.label}
            onDragStart={onStart}
            onDragEnd={onEnd}
            onInsert={onInsert ? () => onInsert(t.value) : undefined}
          />
        ))}
      </RailSection>

      <RailSection label="Elements" hint={FORMA_TIPS.tokenElements}>
        {elemTokens.map((t) => (
          <PaletteChip
            key={t.id}
            value={t.value}
            label={t.label}
            dashed={t.id === 'Space' || t.id === 'Return'}
            onDragStart={onStart}
            onDragEnd={onEnd}
            onInsert={onInsert ? () => onInsert(t.value) : undefined}
          />
        ))}
      </RailSection>

      <RailSection label="Custom" hint={FORMA_TIPS.tokenCustom}>
        <NewCustomChip onStart={onStart} onEnd={onEnd} />
        {savedTokens.map((t, i) => (
          <CustomChip
            key={'s' + i}
            value={t}
            saved
            onRemove={() => onRemoveSaved(t)}
            onStart={onStart}
            onEnd={onEnd}
          />
        ))}
        {recentOnly.map((t, i) => (
          <CustomChip
            key={'r' + i}
            value={t}
            onPin={() => onSave(t)}
            onStart={onStart}
            onEnd={onEnd}
          />
        ))}
      </RailSection>
    </div>
  );
}

/* ─── Project Token Chips — the drag tray at the top of the "Project
       Tokens" palette. Numbered `{P#}` placeholders are the ONLY project
       token mechanism (Josh, 2026-08-13, Order 5 — named "project field"
       chips like Project Name/Number/Client retired; a master's placeholder
       can still BIND to one of those via the "Bind to" dropdown below, but
       the palette itself only ever mints/reuses `{P#}`s and the project's
       own user-defined tokens). ─── */
function ProjectTokenChips({
  nextPlaceholder,
  usedPlaceholders,
  projectTokens,
  onStart,
  onEnd,
  onInsert,
}: {
  /** The next unused `{P#}` for the open forma — the chip mints this one. */
  nextPlaceholder?: string;
  /** Numbered placeholders ALREADY placed somewhere in this forma ("P1",
   *  "P2", …) — one chip each, so dropping/clicking it places the SAME id
   *  again instead of minting a new one ("so I can put placeholder 2 in
   *  multiple locations", Josh 2026-08-13, Order 4). Derived live from the
   *  open forma, so the list is per-forma by construction and a chip
   *  disappears the moment its last instance is removed. */
  usedPlaceholders?: string[];
  /** User-defined tokens of the open PROJECT forma's project (absent for masters). */
  projectTokens?: Array<{ id: string; name: string }>;
  onStart?: () => void;
  onEnd?: () => void;
  /** Optional click-to-insert (Designer). Absent → drag-only, as before. */
  onInsert?: (value: string) => void;
}) {
  return (
    <div
      style={{
        // Natural height; shrinks (and scrolls) only when the panel genuinely
        // runs out of room — never clips while space sits empty below.
        flex: '0 1 auto',
        minHeight: 120,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '12px 12px 14px',
        borderBottom: '1px solid var(--bd)',
      }}
    >
      <RailSection label="Placeholder" hint={FORMA_TIPS.tokenPlaceholder}>
        {/* A blank you fill in per project. Dropping one mints the next free
            number, so P1, P2, P3 stay unique inside a forma. */}
        <PaletteChip
          value={nextPlaceholder || '{P1}'}
          label={`Placeholder ${(nextPlaceholder || '{P1}').slice(1, -1)}`}
          linked
          dashed
          title={FORMA_TIPS.tokenPlaceholder}
          onDragStart={onStart}
          onDragEnd={onEnd}
          onInsert={onInsert ? () => onInsert(nextPlaceholder || '{P1}') : undefined}
        />
        {(usedPlaceholders || []).map((id) => (
          <PaletteChip
            key={`used-${id}`}
            value={`{${id}}`}
            label={`Placeholder ${id}`}
            linked
            dashed
            title={`Placeholder ${id} is already used in this forma — drag or click to place it again in another spot.`}
            onDragStart={onStart}
            onDragEnd={onEnd}
            onInsert={onInsert ? () => onInsert(`{${id}}`) : undefined}
          />
        ))}
      </RailSection>

      {(projectTokens || []).length > 0 && (
        <RailSection
          label="This project"
          hint="Tokens you defined on the project dashboard — they resolve on export."
        >
          {(projectTokens || []).map((t) => (
            <PaletteChip
              key={`pt-${t.id}`}
              value={`{ProjectToken:${t.id}}`}
              label={t.name}
              linked
              title="User-defined project token — set up in the project dashboard, resolves on export"
              onDragStart={onStart}
              onDragEnd={onEnd}
              onInsert={onInsert ? () => onInsert(`{ProjectToken:${t.id}}`) : undefined}
            />
          ))}
        </RailSection>
      )}
    </div>
  );
}

// Expose as globals for legacy compat
(window as any).PaletteChip = PaletteChip;
(window as any).ZoneChip = ZoneChip;
(window as any).DropZone = DropZone;
(window as any).TokenRail = TokenRail;
(window as any).ZoomCluster = ZoomCluster;

export { PaletteChip, ZoneChip, DropZone, TokenRail, ProjectTokenChips, ZoomCluster };
